import React, { useCallback, FC } from "react";
import styled from "@emotion/styled";

import Avatar from "./Avatar";
import Icon, { SyncOutlined, ArrowLeftOutlined, PushpinOutlined } from "src/icons";
import { ReactComponent as FilterIcon } from "src/assets/filter-icon.svg";
import { ReactComponent as ChartCompareIcon } from "src/assets/chart-compare.svg";
import userService from "src/shared/services/user.service";
import { safeGet } from "src/shared/utils";
import { SearchIconHome } from "src/assets/search-icon.js";
import { IconClass } from "src/shared/types";
import colors from "src/styles/variables.scss";
import { HomeOutlined } from "@ant-design/icons";

const TopNavContainer = styled.nav<{ rootStyle?: string }>`
  padding: 15px 0;
  /* display: flex;
  align-items: center; */
  display: grid;
  grid-template-columns: 10% auto auto;
  /* line-height: 24px; */
  letter-spacing: 1px;
  align-items: center;
  > div {
    height: 25px;
  }
  ${({ rootStyle }) => rootStyle && rootStyle};
`;

const MenuButton = styled.div`
  height: 100%;
  width: 100%;
  font-size: 1.6rem;
  max-width: 35px;
`;

const MenuIcon = styled.a`
  height: 2px;
  background-color: white;
  margin: 4px 0;
`;

const NavTitle = styled.h3`
  font-weight: bolder;
  font-family: Arial, "sans-serif";
  font-size: 1.44rem;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: 0.33px;
  color: ${colors.milkyWhite};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin: 0;
`;

const MenuOption = styled.div`
  text-align: right;
  position: relative;
  margin-left: 14px;
  > i {
    font-size: 1.6rem;
  }
`;

const ActionButtonsSection = styled.section`
  display: flex;
  justify-self: end;
  align-items: center;
`;

const IconMarker = styled.i`
  right: -2px;
  position: absolute;
  height: 9px;
  width: 9px;
  background-color: ${colors.downRed};
  border-radius: 50%;
  top: -3px;
  display: inline-block;
`;

type NavbarProps = {
  isHome: boolean;
  isDetails?: boolean;
  handleClick(): void;
  isFilterApplied?: boolean;
  headerText?: string;
  rootStyle?: string;
  onLogout?(): void;
  onPermissionsShow?(): void;
};

export const MenuItem: FC<{
  className: string;
  iconClass: string;
  handleClick(): void;
  showMarker?: boolean;
  animate?: boolean;
}> = ({ className, iconClass, handleClick, showMarker, animate = false }) => {
  const generateIcon = useCallback(
    (iconClass) => {
      switch (iconClass) {
        case IconClass.SEARCH:
          return <SearchIconHome className="text-lg" />;
        case IconClass.SLIDERS:
          return (
            <>
              <Icon component={FilterIcon} className="text-lg" />
              {showMarker && <IconMarker className="text-lg" />}
            </>
          );
        case IconClass.COMPARE:
          return <Icon component={ChartCompareIcon} className="text-lg" />;

        case IconClass.SYNC:
          return <SyncOutlined className="text-lg" />;

        case IconClass.PUSHPIN:
          return <PushpinOutlined className="text-lg" />;
        case IconClass.Home:
          return <HomeOutlined className="text-lg" />;
        default:
          return <Icon type={iconClass} />;
      }
    },
    [iconClass, handleClick, className, showMarker]
  );

  return (
    <MenuOption className={className} onClick={handleClick}>
      {generateIcon(iconClass)}
    </MenuOption>
  );
};
const TopNavbar: FC<NavbarProps> = ({
  isHome,
  handleClick,
  onLogout,
  headerText,
  children,
  rootStyle,
  onPermissionsShow,
}) => {
  return (
    <TopNavContainer rootStyle={rootStyle}>
      <MenuButton onClick={handleClick}>
        {isHome ? (
          <Avatar
            initials={safeGet(userService, "userInfo.initials", "")}
            size="10px"
            onLogout={onLogout}
            onPermissionsShow={onPermissionsShow}
          />
        ) : (
          <ArrowLeftOutlined className="text-lg" />
        )}
      </MenuButton>
      <NavTitle>{headerText}</NavTitle>
      <ActionButtonsSection>{children}</ActionButtonsSection>
    </TopNavContainer>
  );
};

export default TopNavbar;
